import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from "react";

// ========================
// TYPES
// ========================

export type RecurringType = "invoice" | "expense" | "payment";
export type RecurringFrequency = "weekly" | "monthly" | "quarterly" | "yearly";
export type RecurringStatus = "Active" | "Paused" | "Completed";
export type AutoCategoryRule = {
  id: string;
  keyword: string;
  category: string;
  vendor?: string;
  paymentMethod?: string;
  createdAt: string;
};

export interface RecurringEntry {
  id: string;
  type: RecurringType;
  title: string;
  description: string;
  amount: number;
  frequency: RecurringFrequency;
  category: string;
  vendor?: string;
  paymentMethod?: string;
  debitLedger?: string;
  creditLedger?: string;
  startDate: string;
  nextRunDate: string;
  endDate?: string;
  status: RecurringStatus;
  totalGenerated: number;
  lastGeneratedAt?: string;
  createdAt: string;
}

export interface GeneratedEntry {
  id: string;
  recurringId: string;
  recurringTitle: string;
  type: RecurringType;
  amount: number;
  category: string;
  vendor?: string;
  paymentMethod?: string;
  generatedDate: string;
  status: "Generated" | "Posted" | "Failed";
}

// ========================
// SEED DATA
// ========================

const SEED_RECURRING: RecurringEntry[] = [];
const SEED_GENERATED: GeneratedEntry[] = [];
const SEED_RULES: AutoCategoryRule[] = [];

// ========================
// HELPERS
// ========================

function getNextDate(current: string, freq: RecurringFrequency): string {
  const d = new Date(current);
  switch (freq) {
    case "weekly": d.setDate(d.getDate() + 7); break;
    case "monthly": d.setMonth(d.getMonth() + 1); break;
    case "quarterly": d.setMonth(d.getMonth() + 3); break;
    case "yearly": d.setFullYear(d.getFullYear() + 1); break;
  }
  return d.toISOString().split("T")[0];
}

export function autoCategorize(description: string, rules: AutoCategoryRule[]): { category?: string; vendor?: string; paymentMethod?: string } {
  const lower = description.toLowerCase();
  for (const rule of rules) {
    if (lower.includes(rule.keyword.toLowerCase())) {
      return { category: rule.category, vendor: rule.vendor, paymentMethod: rule.paymentMethod };
    }
  }
  return {};
}

// ========================
// CONTEXT
// ========================

interface AutomationContextType {
  recurringEntries: RecurringEntry[];
  generatedEntries: GeneratedEntry[];
  categoryRules: AutoCategoryRule[];
  addRecurring: (entry: Omit<RecurringEntry, "id" | "createdAt" | "totalGenerated" | "lastGeneratedAt" | "nextRunDate"> & { nextRunDate?: string }) => RecurringEntry;
  updateRecurring: (id: string, data: Partial<RecurringEntry>) => void;
  toggleRecurringStatus: (id: string) => void;
  deleteRecurring: (id: string) => void;
  runSchedule: (id: string) => GeneratedEntry | null;
  runAllDue: () => GeneratedEntry[];
  addCategoryRule: (rule: Omit<AutoCategoryRule, "id" | "createdAt">) => void;
  deleteCategoryRule: (id: string) => void;
  autoCategorizeText: (text: string) => { category?: string; vendor?: string; paymentMethod?: string };
  getDueEntries: () => RecurringEntry[];
}

const AutomationContext = createContext<AutomationContextType | null>(null);

export function AutomationProvider({ children }: { children: ReactNode }) {
  const [recurringEntries, setRecurring] = useState<RecurringEntry[]>(SEED_RECURRING);
  const [generatedEntries, setGenerated] = useState<GeneratedEntry[]>(SEED_GENERATED);
  const [categoryRules, setRules] = useState<AutoCategoryRule[]>(SEED_RULES);

  const addRecurring = useCallback((data: Omit<RecurringEntry, "id" | "createdAt" | "totalGenerated" | "lastGeneratedAt" | "nextRunDate"> & { nextRunDate?: string }) => {
    const entry: RecurringEntry = {
      ...data,
      id: `rec-${crypto.randomUUID().slice(0, 6)}`,
      nextRunDate: data.nextRunDate || data.startDate,
      totalGenerated: 0,
      createdAt: new Date().toISOString(),
    };
    setRecurring((prev) => [entry, ...prev]);
    return entry;
  }, []);

  const updateRecurring = useCallback((id: string, data: Partial<RecurringEntry>) => {
    setRecurring((prev) => prev.map((e) => (e.id === id ? { ...e, ...data } : e)));
  }, []);

  const toggleRecurringStatus = useCallback((id: string) => {
    setRecurring((prev) =>
      prev.map((e) => (e.id === id ? { ...e, status: e.status === "Active" ? "Paused" : "Active" } : e))
    );
  }, []);

  const deleteRecurring = useCallback((id: string) => {
    setRecurring((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const runSchedule = useCallback((id: string): GeneratedEntry | null => {
    const entry = recurringEntries.find((e) => e.id === id);
    if (!entry || entry.status !== "Active") return null;

    const generated: GeneratedEntry = {
      id: `gen-${crypto.randomUUID().slice(0, 6)}`,
      recurringId: entry.id,
      recurringTitle: entry.title,
      type: entry.type,
      amount: entry.amount,
      category: entry.category,
      vendor: entry.vendor,
      paymentMethod: entry.paymentMethod,
      generatedDate: entry.nextRunDate,
      status: "Generated",
    };

    setGenerated((prev) => [generated, ...prev]);
    setRecurring((prev) =>
      prev.map((e) =>
        e.id === id
          ? {
              ...e,
              nextRunDate: getNextDate(e.nextRunDate, e.frequency),
              totalGenerated: e.totalGenerated + 1,
              lastGeneratedAt: new Date().toISOString(),
            }
          : e
      )
    );

    return generated;
  }, [recurringEntries]);

  const getDueEntries = useCallback(() => {
    const today = new Date().toISOString().split("T")[0];
    return recurringEntries.filter((e) => e.status === "Active" && e.nextRunDate <= today);
  }, [recurringEntries]);

  const runAllDue = useCallback((): GeneratedEntry[] => {
    const due = getDueEntries();
    const results: GeneratedEntry[] = [];
    for (const entry of due) {
      const gen = runSchedule(entry.id);
      if (gen) results.push(gen);
    }
    return results;
  }, [getDueEntries, runSchedule]);

  const addCategoryRule = useCallback((rule: Omit<AutoCategoryRule, "id" | "createdAt">) => {
    setRules((prev) => [
      { ...rule, id: `rule-${crypto.randomUUID().slice(0, 6)}`, createdAt: new Date().toISOString() },
      ...prev,
    ]);
  }, []);

  const deleteCategoryRule = useCallback((id: string) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const autoCategorizeText = useCallback(
    (text: string) => autoCategorize(text, categoryRules),
    [categoryRules]
  );

  return (
    <AutomationContext.Provider
      value={{
        recurringEntries,
        generatedEntries,
        categoryRules,
        addRecurring,
        updateRecurring,
        toggleRecurringStatus,
        deleteRecurring,
        runSchedule,
        runAllDue,
        addCategoryRule,
        deleteCategoryRule,
        autoCategorizeText,
        getDueEntries,
      }}
    >
      {children}
    </AutomationContext.Provider>
  );
}

export function useAutomation() {
  const ctx = useContext(AutomationContext);
  if (!ctx) throw new Error("useAutomation must be used within AutomationProvider");
  return ctx;
}
